// The custom widget: an agent's own code, drawn in a sealed frame with the
// board's widget kit inside (shared/widgetGuide.ts says what it offers). The
// frame tells the board how it drew, so an error shows on the tile with a way
// to fix it, and the agent that wrote it hears back too.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Copy, TriangleAlert, Wand2, Code2 } from "lucide-react";
import type { WidgetItem } from "../../shared/types";
import { useShallow } from "zustand/react/shallow";
import { recordRow, recordsOf } from "../../shared/records";
import { cleanReport, htmlHash, linksIn, problems, webLink, type RenderReport } from "../../shared/widgetKit";
import { applyRecordOps } from "../boards/custom/records";
import { commit } from "../state/actions";
import { boardEntries, get, set, useStore } from "../state/store";
import { refreshNow } from "../state/sources";
import { reportRender, reportShot } from "../state/sync";
import { handOff, useBuilder, type Builder, askWho } from "../agent/handoff";
import { drawAsk, fixAsk } from "../agent/asks";
import { SPEC } from "../../shared/catalog";
import { buildSrcdoc, fontsNow, loadFonts, payload, readTheme, type Voice } from "./kit";
import { boardTile, grownSize } from "./fit";
import type { WP } from "./data";
import { Setup } from "./setup";
import { fileToSave } from "./saveFile";
import { download } from "../lib/download";

/** Everything about the board's look that this widget's colours depend on. */
function useLookKey(item: WidgetItem): string {
  return useStore((s) => {
    const b = s.boards.find((x) => x.id === item.boardId) ?? s.boards.find((x) => x.id === s.boardId);
    const made = b?.theme.custom ? (s.themes[b.theme.custom]?.updatedAt ?? 0) : 0;
    const tint = s.tintPreview?.ids.includes(item.id) ? s.tintPreview.color : "";
    return `${JSON.stringify(b?.theme ?? null)}|${made}|${s.themePreview ?? ""}|${tint}|${item.style?.accent ?? ""}|${item.style?.tile ?? ""}`;
  });
}

const NONE: never[] = [];
const titleOf = (item: WidgetItem) => item.title || SPEC[item.type].label;

/** Has it fixed, by Claude in the chat or the user's own agent, with what went wrong. */
export function fixIt(item: WidgetItem, report: RenderReport | null) {
  set({ selection: [item.id] });
  handOff(fixAsk(item, report ? problems(report).join(" ") : ""));
}

/** The fix button's words, for whoever will do the fixing. */
export function fixLabel(b: Builder): string {
  return b.mode === "chat" ? `Fix it with ${b.chat}` : b.mode === "agent" ? `Copy a fix for ${b.agent}` : "Fix it";
}

/**
 * The board's keys a widget may pass on: leaving it (Esc), the command bar,
 * the chat, the shortcuts, themes, full screen and all boards. Never a key
 * that deletes, selects, undoes, adds or moves things.
 */
export function safeKey(key: string, mod: boolean, shift: boolean, alt: boolean): boolean {
  if (alt) return false;
  if (key === "Escape") return true;
  if (mod) return !shift && (key.toLowerCase() === "k" || key.toLowerCase() === "j");
  return key === "/" || key === "?" || key === "f" || key === "g" || (shift && (key === "F" || key === "T"));
}

/**
 * An agent's widget whose content runs past its tile gets the room it needs
 * (grownSize says how much, and when not). A tile left as it is reports that
 * it is cut off, so the agent can choose a size.
 */
function growToFit(w: WidgetItem, r: RenderReport, hash: string): boolean {
  const next = grownSize(w, r, hash, Object.values(get().items));
  if (!next) return false;
  commit([{ op: "item.put", item: { ...w, ...next, props: { ...w.props, fitted: hash }, updatedAt: Date.now() } }], "Fit", { history: false });
  return true;
}

/**
 * app: this is a Custom board's app, filling the board. It has no tile to fit and no header, and it is given the
 * board's records (board.records), which it changes only through the kit's messages.
 */
export function Custom({ item, stream, readOnly, app = false }: WP & { app?: boolean }) {
  const html = String(item.props.html ?? "");
  const holder = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const preview = item.boardId === "preview" || item.id.startsWith("preview");
  const live = !readOnly && !preview;
  const header = app ? false : (item.style?.header ?? true);
  const records = useStore(useShallow((s) => (app ? recordsOf(boardEntries(s, item.boardId)) : NONE)));
  const rows = useMemo(() => records.map(recordRow), [records]);
  const look = useLookKey(item);
  const data = useMemo(() => payload(stream), [stream]);
  const stateJson = JSON.stringify(item.props.state ?? {});
  const hash = useMemo(() => htmlHash(html), [html]);
  const [doc, setDoc] = useState<string | null>(null);
  const [build, setBuild] = useState(0);
  const built = useRef<{ voice: Voice; mode: unknown; look: string; state: string } | null>(null);
  const alive = useRef(false); // the kit inside is listening
  const saved = useRef<string | null>(null);
  // a picture of the drawing it has now, taken only when an agent waiting on it asks, after the report, which never
  // waits for it
  type Drawing = { hash: string; size: { w: number; h: number }; stream: string | null };
  const wantShot = useRef(false);
  const shooting = useRef<Drawing | null>(null);
  // the drawing its tile shows, as last reported
  const drawing = useRef<Drawing | null>(null);
  const latest = useRef({ data, look, hash, item, live, rows, app, preview });
  latest.current = { data, look, hash, item, live, rows, app, preview };
  const rendered = useStore((s) => s.renders[item.id]);
  const report = rendered && rendered.hash === hash ? rendered.report : null;

  // the widget's own window, inside the frame that seals it in. Asking a
  // cross-origin frame for a child it does not have yet throws, so count first.
  const inner = (): Window | null => {
    try {
      const w = frame.current?.contentWindow;
      return w && w.length > 0 ? (w[0] as Window) : null;
    } catch {
      return null;
    }
  };
  const post = (m: Record<string, unknown>) => inner()?.postMessage(m, "*");

  // A widget can ask for things in a loop, so what it asks for is paced: a
  // fresh document at most every two seconds (the last ask is never lost),
  // a press at most every 150ms, a save at most every 400ms (the latest wins).
  const pace = useRef({ built: 0, buildTimer: 0 as ReturnType<typeof setTimeout> | 0, down: 0, saved: 0, saveTimer: 0 as ReturnType<typeof setTimeout> | 0, nextSave: null as string | null, file: 0 });
  useEffect(
    () => () => {
      clearTimeout(pace.current.buildTimer || undefined);
      clearTimeout(pace.current.saveTimer || undefined);
    },
    [],
  );
  const rebuild = () => {
    const q = pace.current;
    const wait = q.built + 2000 - Date.now();
    if (wait <= 0) {
      q.built = Date.now();
      setBuild((n) => n + 1);
    } else if (!q.buildTimer)
      q.buildTimer = setTimeout(() => {
        q.buildTimer = 0;
        q.built = Date.now();
        setBuild((n) => n + 1);
      }, wait);
  };
  const flushSave = () => {
    const q = pace.current;
    q.saveTimer = 0;
    const text = q.nextSave;
    q.nextSave = null;
    if (text === null) return;
    q.saved = Date.now();
    saved.current = text;
    const it = get().items[latest.current.item.id];
    if (!it || it.kind !== "widget") return;
    commit([{ op: "item.put", item: { ...it, props: { ...it.props, state: JSON.parse(text) } } }], "Widget", { history: false });
  };

  /** The theme as it is now, sent in without reloading, unless the typeface changed. */
  const syncLook = () => {
    const b = built.current;
    if (!b || !alive.current || b.look === latest.current.look) return;
    const t = readTheme(holder.current);
    // a new typeface, or light for dark, needs a fresh document
    if (t.voice !== b.voice || t.theme.mode !== b.mode) {
      rebuild();
      return;
    }
    b.look = latest.current.look;
    post({ __cb: "theme", vars: t.vars, theme: t.theme });
  };

  // the document: made again when the code changes, when the typeface changes, or when the widget asks
  useLayoutEffect(() => {
    if (!html) {
      setDoc(null);
      built.current = null;
      return;
    }
    const t = readTheme(holder.current);
    const fonts = fontsNow(t.voice);
    if (fonts === null) {
      let gone = false;
      void loadFonts(t.voice).then(() => !gone && setBuild((n) => n + 1));
      return () => {
        gone = true;
      };
    }
    built.current = { voice: t.voice, mode: t.theme.mode, look: latest.current.look, state: stateJson };
    alive.current = false;
    saved.current = null;
    setDoc(buildSrcdoc(html, { look: t, fonts, data: latest.current.data, state: item.props.state ?? {}, readOnly: !live, header, ...(app ? { records: latest.current.rows } : {}) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [html, build, header, live]);

  useLayoutEffect(syncLook, [look]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (alive.current) post({ __cb: "data", data });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.updatedAt]);

  // the board's records changed: this tab's, another computer's, an Undo or an agent's
  useEffect(() => {
    if (app && alive.current) post({ __cb: "records", records: rows });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // its saved state changed somewhere else, in another tab: start again from that
  useEffect(() => {
    const b = built.current;
    if (!b || stateJson === b.state || stateJson === saved.current) return;
    setBuild((n) => n + 1);
  }, [stateJson]);

  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const f = frame.current;
      if (!f || !e.source || e.source !== inner()) return;
      const m = e.data as Record<string, unknown> | null;
      if (!m || typeof m !== "object") return;
      const L = latest.current;
      const n = (x: unknown) => (typeof x === "number" && Number.isFinite(x) ? x : 0);
      switch (m.__cb) {
        case "ready":
          alive.current = true;
          post({ __cb: "data", data: L.data });
          if (L.app) post({ __cb: "records", records: L.rows });
          syncLook();
          break;
        case "records":
          if (L.live && L.app) applyRecordOps(L.item.boardId, m.ops);
          break;
        case "report": {
          const r = cleanReport(m);
          if (!r) return;
          // drawn in its own tile on the board, at its own size: a phone's column draws it at another. A Custom
          // board's app fills the board at any size, so it reports as the size it is kept at
          const tile = L.app ? { w: L.item.w, h: L.item.h } : boardTile(holder.current?.closest<HTMLElement>(".item"), L.item);
          // an agent's widget whose content runs past its tile grows to fit, before anyone hears it was cut off
          if (L.live && tile && !L.app && growToFit(L.item, r, L.hash)) return;
          set((s) => ({ renders: { ...s.renders, [L.item.id]: { hash: L.hash, report: r, at: Date.now() } } }));
          // only its own tile, at its own size, says how it draws: a phone's column is not that
          if (!(L.live && tile)) break;
          // a drawing an agent asked to see before it was done: its picture follows the report
          const shoot = wantShot.current;
          drawing.current = { hash: L.hash, size: tile, stream: L.item.stream ?? null };
          reportRender(L.item.id, L.hash, r, tile);
          if (shoot) {
            wantShot.current = false;
            shooting.current = drawing.current;
            post({ __cb: "shoot" });
          }
          break;
        }
        case "shot": {
          const d = shooting.current;
          if (!d) return;
          shooting.current = null;
          // a browser that could not take one says so, and the agent waiting for it hears the words alone
          reportShot(L.item.id, d.hash, typeof m.png === "string" && m.png.startsWith("data:image/png;base64,") ? m.png : "", d.size, d.stream);
          break;
        }
        case "stale":
          rebuild();
          break;
        case "refresh": {
          // board.refresh(): the owner's own widget reads its source again, with the board's cooldown (Round 7); a shared
          // view, a preview and a stream nothing reads (a brief's, one an agent pushed) do nothing
          if (!L.live) return;
          const pullId = L.item.stream ? get().streams[L.item.stream]?.pullId : null;
          if (pullId && get().pulls[pullId]) void refreshNow(pullId);
          break;
        }
        case "open": {
          // only a link that came with its own data opens, in a new tab: its code cannot make one up to carry anything out
          const url = typeof m.url === "string" ? webLink(m.url) : null;
          if (!url || !linksIn(L.data).has(url)) return;
          window.open(url, "_blank", "noopener,noreferrer");
          break;
        }
        case "download":
        case "copy": {
          // a file the app saves to this computer (board.download, or a Download link in it), or words it copies
          // (board.copy, or its own clipboard writeText): only from a click in this app just now (the page's own
          // activation, and the focus in this frame, so a click elsewhere on the board never counts), one a second,
          // never from a preview; a file only a picture or plain data (saveFile.ts). Why it was not done goes back to
          // the app, as its error, so its agent hears it
          const copy = m.__cb === "copy";
          if (Date.now() - pace.current.file < 1000) return;
          const act = (navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation;
          const why = L.preview ? "nothing is saved from a preview" : !act?.isActive || document.activeElement !== f ? `call it from the user's click in it, as ${copy ? "a Copy" : "an Export"} button does` : null;
          const done = why ? { why } : copy ? (typeof m.text === "string" && m.text.length <= 100_000 ? { text: m.text } : { why: "give it text, up to 100,000 characters" }) : fileToSave(m);
          if ("why" in done) {
            post({ __cb: m.__cb, why: done.why });
            return;
          }
          pace.current.file = Date.now();
          if ("text" in done) void navigator.clipboard?.writeText(done.text).catch(() => null);
          else download(done.name, done.blob);
          break;
        }
        case "save": {
          if (!L.live) return;
          let text: string;
          try {
            text = JSON.stringify(m.state ?? {});
          } catch {
            return;
          }
          if (text.length > 16_000) return;
          const q = pace.current;
          q.nextSave = text;
          if (!q.saveTimer) q.saveTimer = setTimeout(flushSave, Math.max(0, q.saved + 400 - Date.now()));
          break;
        }
        // the board's own gestures, made inside the frame
        case "wheel": {
          const r = f.getBoundingClientRect();
          const k = r.width / (f.offsetWidth || r.width || 1);
          f.dispatchEvent(
            new WheelEvent("wheel", { deltaX: n(m.dx), deltaY: n(m.dy), deltaMode: n(m.mode), ctrlKey: !!m.ctrl, metaKey: !!m.meta, shiftKey: !!m.shift, clientX: r.left + n(m.x) * k, clientY: r.top + n(m.y) * k, bubbles: true, cancelable: true }),
          );
          break;
        }
        case "down": {
          if (!L.live || Date.now() - pace.current.down < 150) return;
          pace.current.down = Date.now();
          // menus close on a press anywhere, this one included
          document.body.dispatchEvent(new PointerEvent("pointerdown", { bubbles: true }));
          const s = get();
          if (L.live && s.tool === "select" && !s.selection.includes(L.item.id)) set({ selection: [L.item.id], editing: null });
          break;
        }
        case "menu": {
          // a right-click the widget's own code left alone: the board's menu for it, as on any tile, from where it was made
          if (!L.live || get().tool !== "select" || Date.now() - pace.current.down > 2000) return;
          const r = f.getBoundingClientRect();
          const k = r.width / (f.offsetWidth || r.width || 1);
          f.dispatchEvent(new MouseEvent("contextmenu", { clientX: r.left + n(m.x) * k, clientY: r.top + n(m.y) * k, button: 2, bubbles: true, cancelable: true }));
          break;
        }
        case "dbl": {
          // a double-click the widget's own code left alone: the widget's settings, as on any tile
          if (!L.live || get().tool !== "select" || Date.now() - pace.current.down > 2000) return;
          set({ selection: [L.item.id], inspect: true, editing: null });
          break;
        }
        case "key": {
          // A widget's code can post these whenever it likes, so a key only
          // reaches the board while the widget really has focus, and only the
          // board's harmless keys: nothing that deletes, selects or adds.
          const key = typeof m.key === "string" ? m.key.slice(0, 20) : "";
          if (!key || document.activeElement !== f) return;
          const mod = !!m.ctrl || !!m.meta;
          if (!safeKey(key, mod, !!m.shift, !!m.alt)) return;
          if (key === "Escape") f.blur();
          // a held key's repeats stay repeats, which the board's shortcuts act on once
          f.dispatchEvent(new KeyboardEvent("keydown", { key, code: typeof m.code === "string" ? m.code.slice(0, 20) : "", ctrlKey: !!m.ctrl, metaKey: !!m.meta, shiftKey: !!m.shift, altKey: !!m.alt, repeat: !!m.repeat, bubbles: true, cancelable: true }));
          break;
        }
      }
    };
    // an agent asks to see how it draws now: the drawing in the tile if it is current, else the next one
    const onShoot = (e: Event) => {
      const L = latest.current;
      if ((e as CustomEvent).detail !== L.item.id || !L.live) return;
      const d = drawing.current;
      if (alive.current && d && d.hash === L.hash && d.size.w === L.item.w && d.size.h === L.item.h && d.stream === (L.item.stream ?? null) && !shooting.current) {
        shooting.current = d;
        post({ __cb: "shoot" });
      } else wantShot.current = true;
    };
    window.addEventListener("message", onMsg);
    window.addEventListener("cb:shoot", onShoot);
    return () => {
      window.removeEventListener("message", onMsg);
      window.removeEventListener("cb:shoot", onShoot);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!html) return <CustomEmpty item={item} readOnly={!live} />;
  const broken = !!report && report.errors.length > 0 && report.blank;
  // content that runs off the bottom fades out there, instead of being cut mid-row
  const cut = !app && !!report && !report.scrolls && report.sh > report.h + 6;
  return (
    <div ref={holder} className={`custom${broken ? " is-broken" : ""}${cut ? " is-cut" : ""}`}>
      {doc && <iframe ref={frame} key={build} className="custom-frame" title={titleOf(item)} sandbox="allow-scripts" srcDoc={doc} />}
      {broken && report && <CustomError item={item} report={report} readOnly={!live} app={app} />}
    </div>
  );
}

function CustomEmpty({ item, readOnly }: { item: WidgetItem; readOnly: boolean }) {
  const builder = useBuilder();
  const who = askWho(builder);
  const title = `${who.charAt(0).toUpperCase() + who.slice(1)} draws this one`;
  if (readOnly)
    return (
      <div className="empty custom-empty">
        <span className="empty-title">{title}</span>
      </div>
    );
  return (
    <Setup icon={<Wand2 />} title={title} sub={`Say what it should show, and ${who} writes it here in your board's own style.`}>
      <button
        className="btn btn-sm btn-primary"
        onClick={() => {
          set({ selection: [item.id] });
          handOff(drawAsk(item));
        }}
      >
        {builder.mode === "agent" ? <Copy size={13} /> : <Wand2 size={13} />} {builder.mode === "agent" ? `Copy for ${builder.agent}` : `Describe it to ${who}`}
      </button>
      <button className="btn btn-sm setup-more" onClick={() => set({ panel: "gallery", panelArg: { cat: "custom" } })}>
        See ideas
      </button>
    </Setup>
  );
}

function CustomError({ item, report, readOnly, app }: { item: WidgetItem; report: RenderReport; readOnly: boolean; app: boolean }) {
  const builder = useBuilder();
  const [details, setDetails] = useState(false);
  const e = report.errors[0];
  return (
    <div className="custom-error" data-interactive>
      <span className="custom-error-ico">
        <TriangleAlert size={16} />
      </span>
      <b>{app ? "This board's app stopped working" : "This widget stopped working"}</b>
      <span className="custom-error-why">{e.line ? `Its code broke on line ${e.line} while it was drawing.` : "Its code broke while it was drawing."}</span>
      {details && (
        <code title={e.message}>
          {e.message}
          {e.line ? <i> · line {e.line}</i> : null}
        </code>
      )}
      {!readOnly && (
        <div className="empty-actions">
          <button className="btn btn-sm btn-primary" onClick={() => fixIt(item, report)}>
            {builder.mode === "agent" ? <Copy size={13} /> : <Wand2 size={13} />} {fixLabel(builder)}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => setDetails(!details)} aria-expanded={details}>
            {details ? "Hide details" : "Details"}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => set({ selection: [item.id], codeFor: item.id })} aria-label="See the code" title="See the code">
            <Code2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
